import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Shield, Lock, Download, RefreshCw, LogOut, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ADMIN_TOKEN_KEY = 'cl_admin_token';

export const AdminPage = () => {
  const { toast } = useToast();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const saved = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (saved) {
      setToken(saved);
      fetchWaitlist(saved);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/login`, { password });
      const t = res.data.token;
      sessionStorage.setItem(ADMIN_TOKEN_KEY, t);
      setToken(t);
      await fetchWaitlist(t);
      toast({ title: 'Welcome back!', description: 'Successfully logged in.' });
    } catch (err) {
      toast({
        title: 'Login failed',
        description: err.response?.data?.detail || 'Wrong password.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlist = async (authToken) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/waitlist`, {
        headers: { 'X-Admin-Token': authToken }
      });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        toast({
          title: 'Session expired',
          description: 'Please log in again.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Could not load waitlist',
          description: err.message,
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken('');
    setPassword('');
    setEntries([]);
    setTotal(0);
  };

  const exportCSV = () => {
    if (entries.length === 0) return;
    const header = 'Name,Email,Age,Location,Signed Up\n';
    const rows = entries.map(e => `"${e.name}","${e.email}","${e.age || ''}","${(e.location || '').replace(/"/g, '""')}","${e.created_at}"`).join('\n');
    const csv = header + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `connect-legacy-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Login Screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 bg-[#7E33E0]/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-7 w-7 text-[#7E33E0]" />
            </div>
            <CardTitle className="text-2xl">Admin Access</CardTitle>
            <CardDescription>Enter your password to view the waitlist</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Dashboard Screen ----
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/assets/cl-heart.png" alt="Connect Legacy" className="h-12 w-12 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-gray-900">Connect Legacy</h1>
              <p className="text-xs text-gray-500">Admin Dashboard</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="text-gray-600 hover:text-gray-900"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Waitlist Signups</h2>
            <p className="text-gray-600 mt-1">People interested in Connect Legacy</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fetchWaitlist(token)}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={exportCSV}
              disabled={entries.length === 0}
              data-testid="export-csv-btn"
              className="bg-[#7E33E0] hover:bg-[#6B2BC0]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Signups</CardTitle>
            <Users className="h-5 w-5 text-[#7E33E0]" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-gray-900" data-testid="total-signups">{total}</div>
          </CardContent>
        </Card>

        {/* Entries Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Signups</CardTitle>
            <CardDescription>Sorted by most recent first</CardDescription>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No signups yet. Share your landing page to get started!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">#</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Age</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Location</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Signed Up</th>
                    </tr>
                  </thead>
                  <tbody data-testid="waitlist-table-body">
                    {entries.map((entry, idx) => (
                      <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-500">{idx + 1}</td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{entry.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          <a href={`mailto:${entry.email}`} className="text-[#7E33E0] hover:underline">
                            {entry.email}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">{entry.age || '—'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{entry.location || '—'}</td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {new Date(entry.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
